import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import "./VillageSection.css";
import PageHeader from "./PageHeader";
import { useState, useEffect } from 'react';
import axiosInstance from '../services/axios';
import natureIcon from '../assets/icons/nature.svg'



/**
 * VillageSection
 *
 * Props:
 * - title: string (main title)
 * - paragraphs: array of strings (each paragraph of the left column)
 * - mainImage: string (URL for the right-side image) - leave empty or null for placeholder
 * - stats: array of objects [{ icon: ReactNode (optional), value: string, label: string }]
 */
function VillageSection({
  title = "आमची ग्राम पंचायत",
  paragraphs = [
    "चिरकालीन गाव, प्राचीन सांस्कृतिक परंपरा आणि ऐतिहासिक ठिकाणे यांचा संगम आहे. गावाच्या इतिहासात अनेक घटना आणि परंपरा नोंदविल्या गेल्या आहेत.",
    "गावाच्या आसपास अनेक प्राचीन मंदिरे असून, येथील उत्सव आणि धार्मिक कार्यक्रम लोकांमध्ये खूप लोकप्रिय आहेत.",
    "गावातील लोकसंख्या विविध जाती-धर्मांवर आधारित आहे आणि तेथे अनेक पारंपरिक व्यवसाय आढळतात."
  ],
  mainImage = "",
  stats = [
    { value: "1957", label: "स्थापनेचे वर्ष", icon: null },
    { value: "12,458", label: "लोकसंख्या", icon: null },
    { value: "87.5%", label: "साक्षरता दर", icon: null },
    { value: "12 km²", label: "क्षेत्र", icon: null }
  ]
}) {
  const { t } = useTranslation();
  // Clamp to 1000 characters and show read more if needed, but preserve paragraph structure
  const maxChars = 1000;
  const [expanded, setExpanded] = useState(false);
  // merged stats prefer API values when available
  // eslint-disable-next-line no-unused-vars
  const [mergedStats, setMergedStats] = useState(null);

  useEffect(() => {
    let mounted = true;
    axiosInstance
      .get('/figures/')
      .then((resp) => {
        if (!mounted) return;
        let data = resp && resp.data ? resp.data : [];
        if (!Array.isArray(data)) data = data.results || data.data || Object.values(data) || [];

        if (!Array.isArray(data) || data.length === 0) return;

        // build a simple lookup by normalized name/title/label
        const normalize = (s) => (s || '').toString().trim().toLowerCase();
        const lookup = {};
        data.forEach((item) => {
          const key = normalize(item.title || item.name || item.label);
          if (key) lookup[key] = item;
        });

        // Create merged stats array by mapping incoming `stats` prop
        const merged = stats.map((s) => {
          const labelNorm = normalize(s.label);
          // exact match first
          let match = lookup[labelNorm];

          // fallback: find any API item whose title/name/label includes the stat label or vice-versa
          if (!match) {
            match = data.find((it) => {
              const combined = normalize([it.title, it.name, it.label].join(' '));
              return (
                (combined && combined.includes(labelNorm)) ||
                (labelNorm && labelNorm.includes(combined))
              );
            });
          }

          // if (match) {
          //   const raw = match.value !== undefined && match.value !== null ? String(match.value) : '';
          //   const unit = match.unit ? String(match.unit) : '';
          //   return { ...s, value: raw + unit };
          // }

          return s;
        });

        setMergedStats(merged);
      })
      .catch(() => {})
      .finally(() => {});

    return () => {
      mounted = false;
    };
  }, [stats]);
  // Always work with an array of paragraphs
  const paraArr = Array.isArray(paragraphs) ? paragraphs : [paragraphs];
  // Join with a unique delimiter
  const DELIM = '|||PARA|||';
  const fullText = paraArr.join(DELIM);
  const shouldTruncate = fullText.length > maxChars;
  const clampedText = shouldTruncate && !expanded ? fullText.slice(0, maxChars) : fullText;
  // Split back into paragraphs, but only show full paragraphs for the last one if clamped
  let displayParas = clampedText.split(DELIM);
  // If clamped and the last paragraph is cut, add ellipsis
  if (shouldTruncate && !expanded) {
    const lastIdx = displayParas.length - 1;
    displayParas[lastIdx] = displayParas[lastIdx] + '...';
  }

  return (
    <section
      className="village-section section-padding"
      aria-label="Village overview section"
    >
      <PageHeader
        breadcrumb={[
          <Link key="home" to="/">{t('home.breadcrumb')}</Link>,
          <span key="gram-panchayat">{t('gramPanchayat.breadcrumb')}</span>
        ]}
        title={t('gramPanchayat.title')}
        description={t('gramPanchayat.description')}
      />
      <div className="village-container">

        <div className="card">
          <div className="card-inner">
            <div className="left-col">
              <div className="left-header flex items-center">
                <div 
                  aria-hidden="true"
                  style={{
                    width: '24px',
                    height: '24px',
                    backgroundColor: 'var(--color-primary)',
                    WebkitMaskImage: `url(${natureIcon})`,
                    WebkitMaskSize: 'contain',
                    WebkitMaskRepeat: 'no-repeat',
                    WebkitMaskPosition: 'center',
                    maskImage: `url(${natureIcon})`,
                    maskSize: 'contain',
                    maskRepeat: 'no-repeat',
                    maskPosition: 'center'
                  }}
                />
                <h2 className="left-heading m-0">{t('Village History') || "गावाचा इतिहास"}</h2>
              </div>

              <div className="history-text">
                {displayParas.map((p, i) => (
                  <p key={i}>
                    {p}
                    {i === displayParas.length - 1 && shouldTruncate && !expanded && (
                      <span className="read-more" style={{ color: 'var(--color-primary)', cursor: 'pointer', marginLeft: 8 }} onClick={() => setExpanded(true)}>
                        अधिक वाचा
                      </span>
                    )}
                    {i === displayParas.length - 1 && shouldTruncate && expanded && (
                      <span className="read-more" style={{ color: 'var(--color-primary)', cursor: 'pointer', marginLeft: 8 }} onClick={() => setExpanded(false)}>
                        कमी दाखवा
                      </span>
                    )}
                  </p>
                ))}
              </div>
            </div>

            <div className="right-col">
              {mainImage ? (
                <img loading="lazy" className="village-image" src={mainImage} alt="Village" />
              ) : (
                <div className="image-placeholder" role="img" aria-label="Image placeholder">
                  <svg width="160" height="100" viewBox="0 0 160 100" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <rect width="160" height="100" rx="8" fill="#e9efe8" />
                    <g fill="#c8d7c5">
                      <rect x="12" y="18" width="36" height="8" rx="2" />
                      <rect x="12" y="34" width="28" height="6" rx="2" />
                    </g>
                  </svg>
                </div>
              )}
            </div>
          </div>
{/* 
          <div className="stats-row">
            {(mergedStats && mergedStats.length ? mergedStats : stats).map((s, idx) => (
              <div key={idx} className="stat-card">
                <div className="stat-icon">
                  {s.icon ? (
                    s.icon
                  ) : (
                    <div className={`stat-dot dot-${(idx % 4) + 1}`} aria-hidden="true" />
                  )}
                </div>

                <div className="stat-value">{s.value}</div>
                <div className="stat-label">{s.label}</div>
              </div>
            ))}
          </div> */}
        </div>
      </div>
    </section>
  );
}

export default VillageSection;